import axios from 'axios';

const API_KEY = import.meta.env?.VITE_ANTHROPIC_API_KEY;
const BASE_URL = 'https://api.anthropic.com/v1/messages';
const MODEL = 'claude-sonnet-4-5-20250929';

/**
 * Maps Anthropic API error status codes to user-friendly error messages.
 * @param {number} statusCode - The HTTP status code from Anthropic API.
 * @param {Object} errorData - The error data from API response.
 * @returns {Object} Object with isInternal flag and error message.
 */
function getErrorMessage(statusCode, errorData) {
  if (statusCode === 401) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your Anthropic API key.' };
  } else if (statusCode === 403) {
    return { isInternal: true, message: 'Permission denied. Your API key does not have access to the specified resource.' };
  } else if (statusCode === 404) {
    return { isInternal: true, message: 'Resource not found. The requested endpoint or model may not exist.' };
  } else if (statusCode === 429) {
    return { isInternal: true, message: 'Rate limit exceeded. You are sending requests too quickly. Please wait a moment and try again.' };
  } else if (statusCode === 500) {
    return { isInternal: true, message: 'Anthropic service error. An unexpected error occurred on their servers. Please try again later.' };
  } else if (statusCode === 529) {
    return { isInternal: true, message: 'Anthropic service is temporarily overloaded. Please try again in a few moments.' };
  } else {
    return { isInternal: false, message: errorData?.error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

/**
 * Makes a request to the Anthropic API
 * @param {Array} messages - Array of message objects with role and content
 * @param {number} maxTokens - Maximum tokens to generate
 * @returns {Promise<string>} - The AI response text
 */
async function makeAnthropicRequest(messages, maxTokens = 2048) {
  if (!API_KEY || API_KEY === 'your-anthropic-api-key-here') {
    throw new Error('Anthropic API key is not configured. Please add VITE_ANTHROPIC_API_KEY to your .env file.');
  }

  const headers = {
    'Content-Type': 'application/json',
    'x-api-key': API_KEY,
    'anthropic-version': '2023-06-01',
    'anthropic-dangerous-direct-browser-access': 'true',
  };

  const data = {
    model: MODEL,
    messages,
    max_tokens: maxTokens,
    temperature: 0.7,
  };

  try {
    const response = await axios?.post(BASE_URL, data, { headers });
    return response?.data?.content?.[0]?.text || '';
  } catch (error) {
    const statusCode = error?.response?.status;
    const errorData = error?.response?.data;
    const errorInfo = getErrorMessage(statusCode, errorData);
    const err = new Error(errorInfo.message);
    err.statusCode = statusCode;
    
    if (!errorInfo?.isInternal) {
      console.error('Anthropic API error:', err);
    }
    throw err;
  }
}

/**
 * Analyzes workout performance data and generates insights
 * @param {Object} performanceData - Object containing workout statistics
 * @returns {Promise<Object>} - Analysis results with insights and recommendations
 */
export async function analyzePerformanceData(performanceData) {
  const {
    overallAdherence,
    averageCompletion,
    effectiveness,
    totalScheduled,
    totalCompleted,
    clientStats,
    dateRange
  } = performanceData;

  const prompt = `You are an expert fitness coach analyzing workout performance data. Provide a comprehensive analysis with actionable insights.

**Performance Metrics (${dateRange}):**
- Overall Adherence Rate: ${overallAdherence}%
- Average Completion Rate: ${averageCompletion}%
- Training Effectiveness: ${effectiveness}%
- Total Scheduled Workouts: ${totalScheduled}
- Total Completed Workouts: ${totalCompleted}
- Active Clients: ${clientStats?.length || 0}

**Client Statistics:**
${clientStats?.slice(0, 10)?.map(client => 
  `- ${client?.fullName || 'Client'}: ${client?.stats?.adherenceRate}% adherence, ${client?.stats?.currentStreak} day streak, ${client?.stats?.completedWorkouts}/${client?.stats?.totalWorkouts} completed`
)?.join('\n')}

Provide:
1. **Overall Performance Summary** (2-3 sentences)
2. **Key Strengths** (2-3 bullet points)
3. **Areas for Improvement** (2-3 bullet points)
4. **Actionable Recommendations** (3-5 specific strategies)
5. **Client Engagement Insights** (patterns and trends)

Keep the analysis professional, data-driven, and actionable for personal trainers.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const analysis = await makeAnthropicRequest(messages, 2048);
    return {
      success: true,
      analysis,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Generates personalized coaching recommendations for a specific client
 * @param {Object} clientData - Client information and workout history
 * @returns {Promise<Object>} - Personalized coaching recommendations
 */
export async function generateClientRecommendations(clientData) {
  const {
    clientName,
    adherenceRate,
    currentStreak,
    completedWorkouts,
    totalWorkouts,
    recentWorkouts,
    goals
  } = clientData;

  const prompt = `You are a personal trainer creating personalized coaching recommendations for a client.

**Client Profile:**
- Name: ${clientName}
- Adherence Rate: ${adherenceRate}%
- Current Streak: ${currentStreak} days
- Completed Workouts: ${completedWorkouts}/${totalWorkouts}
${goals ? `- Goals: ${goals}` : ''}

**Recent Workout History:**
${recentWorkouts?.slice(0, 5)?.map(w => 
  `- ${w?.workoutName}: ${w?.status} ${w?.completedAt ? `on ${new Date(w.completedAt)?.toLocaleDateString()}` : ''}`
)?.join('\n')}

Provide personalized coaching recommendations:
1. **Progress Assessment** (2-3 sentences on current performance)
2. **Motivational Message** (encouraging and specific to their progress)
3. **Specific Action Items** (3-5 concrete next steps)
4. **Workout Adjustments** (if needed based on adherence patterns)
5. **Engagement Strategies** (how to maintain or improve consistency)

Keep recommendations positive, specific, and actionable.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const recommendations = await makeAnthropicRequest(messages, 1536);
    return {
      success: true,
      recommendations,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Analyzes adherence patterns and identifies trends
 * @param {Array} workoutHistory - Array of workout records
 * @returns {Promise<Object>} - Pattern analysis and insights
 */
export async function analyzeAdherencePatterns(workoutHistory) {
  const completedWorkouts = workoutHistory?.filter(w => w?.status === 'completed');
  const missedWorkouts = workoutHistory?.filter(w => w?.status === 'missed' || w?.status === 'skipped');
  
  // Group by day of week
  const dayOfWeekStats = {};
  completedWorkouts?.forEach(w => {
    const day = new Date(w.completedAt)?.getDay();
    dayOfWeekStats[day] = (dayOfWeekStats?.[day] || 0) + 1;
  });

  const prompt = `Analyze workout adherence patterns and provide insights.

**Workout Statistics:**
- Total Workouts: ${workoutHistory?.length}
- Completed: ${completedWorkouts?.length}
- Missed/Skipped: ${missedWorkouts?.length}
- Completion Rate: ${Math.round((completedWorkouts?.length / workoutHistory?.length) * 100)}%

**Day of Week Completion:**
${Object.entries(dayOfWeekStats)?.map(([day, count]) => {
  const days = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  return `- ${days?.[day]}: ${count} workouts`;
})?.join('\n')}

Provide:
1. **Pattern Identification** (what trends do you see?)
2. **Optimal Training Days** (when is adherence highest?)
3. **Risk Factors** (patterns that indicate potential dropout)
4. **Scheduling Recommendations** (how to optimize workout timing)

Be specific and data-driven.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const analysis = await makeAnthropicRequest(messages, 1536);
    return {
      success: true,
      analysis,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Generates progress summary and milestone recognition
 * @param {Object} progressData - Client progress metrics
 * @returns {Promise<Object>} - Progress summary with achievements
 */
export async function generateProgressSummary(progressData) {
  const {
    clientName,
    startDate,
    totalWorkouts,
    completedWorkouts,
    currentStreak,
    longestStreak,
    improvementMetrics
  } = progressData;

  const prompt = `Create an encouraging progress summary for a fitness client.

**Client Progress:**
- Name: ${clientName}
- Training Since: ${startDate}
- Total Workouts Completed: ${completedWorkouts}
- Current Streak: ${currentStreak} days
- Longest Streak: ${longestStreak} days
${improvementMetrics ? `- Improvements: ${improvementMetrics}` : ''}

Generate:
1. **Celebration Message** (recognize achievements and milestones)
2. **Progress Highlights** (specific accomplishments)
3. **Next Milestones** (upcoming goals to work toward)
4. **Motivational Closing** (keep them engaged and excited)

Make it personal, enthusiastic, and specific to their journey.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const summary = await makeAnthropicRequest(messages, 1024);
    return {
      success: true,
      summary,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

export default {
  analyzePerformanceData,
  generateClientRecommendations,
  analyzeAdherencePatterns,
  generateProgressSummary
};

/**
 * Analyzes client performance data and generates personalized workout adjustment recommendations
 * @param {Object} clientPerformanceData - Client workout history and performance metrics
 * @returns {Promise<Object>} - Workout adjustment recommendations
 */
export async function generateWorkoutAdjustments(clientPerformanceData) {
  const {
    clientName,
    adherenceRate,
    currentStreak,
    completedWorkouts,
    totalWorkouts,
    recentWorkouts,
    currentWorkoutPlan,
    fitnessGoals,
    performanceTrends
  } = clientPerformanceData;

  const workoutDetails = currentWorkoutPlan?.exercises?.map(ex => 
    `${ex?.name} - ${ex?.sets}x${ex?.reps || ex?.durationSeconds + 's'} (${ex?.category})`
  )?.join('\n') || 'No current workout plan';

  const recentPerformance = recentWorkouts?.slice(0, 5)?.map(w => 
    `${w?.workoutName}: ${w?.status} ${w?.completedAt ? `on ${new Date(w.completedAt)?.toLocaleDateString()}` : ''} ${w?.notes ? `- Notes: ${w?.notes}` : ''}`
  )?.join('\n') || 'No recent workout history';

  const prompt = `You are an expert personal trainer analyzing a client's workout performance to provide personalized workout adjustment recommendations.

**Client Profile:**
- Name: ${clientName}
- Adherence Rate: ${adherenceRate}%
- Current Streak: ${currentStreak} days
- Completed Workouts: ${completedWorkouts}/${totalWorkouts}
${fitnessGoals ? `- Fitness Goals: ${fitnessGoals}` : ''}
${performanceTrends ? `- Performance Trends: ${performanceTrends}` : ''}

**Current Workout Plan:**
${workoutDetails}

**Recent Workout History:**
${recentPerformance}

Based on this data, provide comprehensive workout adjustment recommendations:

1. **Performance Analysis** (2-3 sentences assessing current progress and adherence patterns)

2. **Workout Intensity Adjustments**
   - Should exercises be progressed (increased difficulty) or regressed (decreased difficulty)?
   - Specific recommendations for sets, reps, or duration modifications
   - Rest period adjustments if needed

3. **Exercise Selection Recommendations**
   - Which exercises should be kept, modified, or replaced?
   - New exercise suggestions based on goals and progress
   - Muscle group balance considerations

4. **Volume & Frequency Adjustments**
   - Should workout frequency increase or decrease?
   - Total volume recommendations (sets per muscle group)
   - Recovery considerations

5. **Personalized Modifications**
   - Specific adjustments based on adherence patterns
   - Motivation strategies if adherence is low
   - Progressive overload strategies if adherence is high

6. **Next Steps Action Plan**
   - 3-5 specific, actionable changes to implement immediately
   - Timeline for reassessment

Be specific, evidence-based, and practical. Focus on sustainable adjustments that align with the client's adherence patterns and goals.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const recommendations = await makeAnthropicRequest(messages, 2560);
    return {
      success: true,
      recommendations,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Predicts goal achievement probability for clients based on historical data
 * @param {Object} clientData - Client performance and goal data
 * @returns {Promise<Object>} - Prediction results with probability scores and confidence intervals
 */
export async function predictGoalAchievement(clientData) {
  const {
    clientName,
    goalType,
    targetDate,
    currentProgress,
    historicalAdherence,
    workoutHistory,
    baselineMetrics,
    progressRate
  } = clientData;

  const prompt = `You are an AI fitness coach analyzing client data to predict goal achievement probability.

**Client Profile:**
- Name: ${clientName}
- Goal: ${goalType}
- Target Date: ${targetDate}
- Current Progress: ${currentProgress}%
- Historical Adherence: ${historicalAdherence}%
- Progress Rate: ${progressRate || 'N/A'}

**Workout History (Last 30 Days):**
${workoutHistory?.slice(0, 10)?.map(w => 
  `- ${w?.workoutName}: ${w?.status} ${w?.completedAt ? `(${new Date(w.completedAt)?.toLocaleDateString()})` : ''}`
)?.join('\n')}

**Baseline Metrics:**
${baselineMetrics ? Object.entries(baselineMetrics)?.map(([key, value]) => `- ${key}: ${value}`)?.join('\n') : 'Not available'}

Provide a comprehensive goal achievement prediction:

1. **Achievement Probability** (0-100% with confidence interval)
2. **Key Success Factors** (3-5 factors supporting achievement)
3. **Risk Factors** (3-5 factors that could prevent achievement)
4. **Trajectory Analysis** (current pace vs required pace)
5. **Milestone Predictions** (when key milestones will be reached)
6. **Confidence Level** (High/Medium/Low with reasoning)

Be data-driven, realistic, and provide actionable insights.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const prediction = await makeAnthropicRequest(messages, 2048);
    return {
      success: true,
      prediction,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Identifies at-risk clients based on adherence patterns and engagement metrics
 * @param {Array} clientsData - Array of client performance data
 * @returns {Promise<Object>} - Risk assessment with prioritized client list
 */
export async function identifyAtRiskClients(clientsData) {
  const clientSummaries = clientsData?.map(client => {
    const recentTrend = client?.adherenceTrend || 'stable';
    const missedStreak = client?.missedWorkoutStreak || 0;
    
    return `- ${client?.fullName}: ${client?.adherenceRate}% adherence, ${missedStreak} missed in a row, trend: ${recentTrend}, last activity: ${client?.lastActivityDate || 'N/A'}`;
  })?.join('\n');

  const prompt = `You are an AI fitness coach analyzing client engagement patterns to identify at-risk clients who may need intervention.

**Client Performance Data:**
${clientSummaries}

**Risk Indicators to Consider:**
- Declining adherence patterns (downward trend)
- Extended periods of inactivity (>7 days)
- Missed workout streaks (3+ consecutive misses)
- Low adherence rates (<60%)
- Sudden drops in engagement
- Inconsistent workout completion

Provide a comprehensive risk assessment:

1. **High-Risk Clients** (immediate intervention needed)
   - List clients with risk score and primary concern
   - Explain why they're at risk

2. **Medium-Risk Clients** (monitoring recommended)
   - List clients with warning signs
   - Explain potential issues

3. **Low-Risk Clients** (stable/thriving)
   - Briefly note positive patterns

4. **Risk Patterns Identified** (common themes across at-risk clients)

5. **Early Warning Signs** (indicators to watch for future risk)

Prioritize clients by urgency and provide specific reasoning for each risk assessment.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const riskAssessment = await makeAnthropicRequest(messages, 2048);
    return {
      success: true,
      riskAssessment,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}

/**
 * Generates targeted intervention recommendations for at-risk clients
 * @param {Object} clientData - Detailed client data for intervention planning
 * @returns {Promise<Object>} - Personalized intervention strategies
 */
export async function generateInterventionRecommendations(clientData) {
  const {
    clientName,
    riskLevel,
    primaryConcerns,
    adherenceRate,
    missedWorkoutStreak,
    lastContactDate,
    communicationHistory,
    workoutPreferences,
    barriers
  } = clientData;

  const prompt = `You are an expert fitness coach creating a targeted intervention plan for an at-risk client.

**Client Profile:**
- Name: ${clientName}
- Risk Level: ${riskLevel}
- Current Adherence: ${adherenceRate}%
- Missed Workout Streak: ${missedWorkoutStreak} workouts
- Last Contact: ${lastContactDate || 'Unknown'}
- Primary Concerns: ${primaryConcerns || 'Declining engagement'}

**Additional Context:**
${workoutPreferences ? `- Workout Preferences: ${workoutPreferences}` : ''}
${barriers ? `- Known Barriers: ${barriers}` : ''}
${communicationHistory ? `- Recent Communication: ${communicationHistory}` : ''}

Create a comprehensive intervention plan:

1. **Immediate Actions** (next 24-48 hours)
   - Specific outreach strategies
   - Communication approach and tone
   - Initial conversation topics

2. **Workout Modifications** (if applicable)
   - Adjust intensity, duration, or frequency
   - Alternative exercise options
   - Barrier-specific adaptations

3. **Motivation Strategies** (personalized to client)
   - Psychological approaches
   - Goal reframing techniques
   - Accountability mechanisms

4. **Engagement Tactics** (rebuild consistency)
   - Short-term wins to target
   - Progress celebration opportunities
   - Check-in schedule

5. **Long-Term Support Plan** (prevent future risk)
   - Sustainable habit-building strategies
   - Early warning monitoring
   - Ongoing support structure

Make recommendations specific, actionable, and empathetic. Focus on re-engagement without judgment.`;

  const messages = [
    { role: 'user', content: prompt }
  ];

  try {
    const interventions = await makeAnthropicRequest(messages, 2048);
    return {
      success: true,
      interventions,
      timestamp: new Date()?.toISOString()
    };
  } catch (error) {
    return {
      success: false,
      error: error?.message,
      timestamp: new Date()?.toISOString()
    };
  }
}